import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { OrgMembersService } from '@/services/orgMembersService';
import type { OrgRole } from '@/types/orgMembers';

import { useKnowledgeIdentity } from './useKnowledgeIdentity';

// Keys carry the tenant and user so switching organization never shows the
// previous organization's members.
export const orgKey = (identityKey: string) =>
  ['org', identityKey, 'info'] as const;
export const orgMembersKey = (identityKey: string) =>
  ['org', identityKey, 'members'] as const;

export function useOrganization() {
  const identity = useKnowledgeIdentity();
  return useQuery({
    queryKey: orgKey(identity.key),
    queryFn: () => OrgMembersService.organization(),
    enabled: identity.ready,
    staleTime: 60_000,
    retry: false,
  });
}

export function useOrgMembers(enabled = true) {
  const identity = useKnowledgeIdentity();
  return useQuery({
    queryKey: orgMembersKey(identity.key),
    queryFn: () => OrgMembersService.members(),
    enabled: identity.ready && enabled,
    staleTime: 0,
    retry: false,
  });
}

function useInvalidateMembers() {
  const identity = useKnowledgeIdentity();
  const queryClient = useQueryClient();
  return () =>
    queryClient.invalidateQueries({ queryKey: orgMembersKey(identity.key) });
}

export function useChangeMemberRole() {
  const invalidate = useInvalidateMembers();
  return useMutation({
    mutationFn: ({ memberId, role }: { memberId: string; role: OrgRole }) =>
      OrgMembersService.changeRole(memberId, role),
    onSettled: invalidate,
  });
}

export function useRemoveMember() {
  const invalidate = useInvalidateMembers();
  return useMutation({
    mutationFn: (memberId: string) => OrgMembersService.remove(memberId),
    onSettled: invalidate,
  });
}

export function useInviteMember() {
  const invalidate = useInvalidateMembers();
  return useMutation({
    mutationFn: ({ email, role }: { email: string; role: OrgRole }) =>
      OrgMembersService.invite(email, role),
    onSettled: invalidate,
  });
}

export function useRevokeInvitation() {
  const invalidate = useInvalidateMembers();
  return useMutation({
    mutationFn: (invitationId: string) =>
      OrgMembersService.revokeInvitation(invitationId),
    onSettled: invalidate,
  });
}

export function useAcceptInvitation() {
  return useMutation({
    mutationFn: (invitationId: string) =>
      OrgMembersService.acceptInvitation(invitationId),
  });
}
